import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { PeerOptions } from '../../../hooks/usePeers';
import { applyContentReviewTypesToFilters, deriveContentReviewTypes } from '../utils/contentReview';

const LOCAL_STORAGE_KEY = 'peer_listing_filters';

const saveFiltersToStorage = (filters: PeerOptions) => {
  try {
    const { page: _page, ...rest } = filters;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(rest));
  } catch {
    // ignore storage errors
  }
};

const loadFiltersFromStorage = (): PeerOptions | null => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PeerOptions) : null;
  } catch {
    return null;
  }
};

/** Applied when the page is loaded with no query parameters and no saved state. */
const DEFAULT_FILTERS: PeerOptions = {
  limit: 25,
  page: 1,
  userType: ['onboardingpeer', 'peer', 'onboardinglistener', 'peerlistener'],
  isPeerActive: ['true'],
  testUsers: ['false'],
  hideIncompletePeers: true,
};

const ALL_USER_TYPES = ['onboardingpeer', 'peer', 'onboardinglistener', 'peerlistener'];

const parseStringArray = (param: string | null): string[] => {
  if (!param) return [];
  return param
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

const parseNumberArray = (param: string | null): number[] => {
  if (!param) return [];
  return param
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n));
};

const parseNumber = (param: string | null): number | undefined => {
  if (!param) return undefined;
  const n = parseInt(param, 10);
  return isNaN(n) ? undefined : n;
};

const parseBoolean = (param: string | null): boolean | undefined => {
  if (param === 'true') return true;
  if (param === 'false') return false;
  return undefined;
};

/**
 * Parse filters strictly from the URL — no silent defaults.
 * Every filter reflects only what is actually in the query string.
 */
const parseFiltersFromSearch = (search: string): PeerOptions => {
  const p = new URLSearchParams(search);

  const userType = parseStringArray(p.get('user_type'));
  const isPeerActive = parseStringArray(p.get('is_peer_active'));
  const testUsers = parseStringArray(p.get('test_users'));
  const anyReviewTypes = parseStringArray(p.get('needs_any_content_review_types')).map((s) => s.toLowerCase());

  const base: PeerOptions = {
    limit: 25,
    page: parseInt(p.get('page') || '1', 10),
    search: p.get('search') || undefined,
    // Fall back to all user types when the param is absent (same UX as before)
    userType: userType.length > 0 ? userType : ALL_USER_TYPES,
    // No silent defaults — only set if the param is present
    isPeerActive: isPeerActive.length > 0 ? isPeerActive : undefined,
    testUsers: testUsers.length > 0 ? testUsers : undefined,
    hideIncompletePeers: parseBoolean(p.get('hide_incomplete_peers')),
    hasBackgroundTags: parseBoolean(p.get('has_background_tags')),
    groupName: p.get('group_name') || undefined,
    tierId: parseNumberArray(p.get('tier_ids')),
    ageGroup: parseStringArray(p.get('age_group')),
    genderTags: parseNumberArray(p.get('gender_tags')),
    ethnicityTags: parseNumberArray(p.get('ethnicity_tags')),
    familyTags: parseNumberArray(p.get('family_tags')),
    languageTags: parseNumberArray(p.get('language_tags')),
    relationshipTags: parseNumberArray(p.get('relationship_tags')),
    spiritualityTags: parseNumberArray(p.get('spirituality_tags')),
    contentTagIds: parseNumberArray(p.get('content_tag_ids')),
    clientIds: parseNumberArray(p.get('client_ids')),
    domainId: parseNumber(p.get('domain_id')),
    tagIds: parseNumberArray(p.get('tag_ids')),
    contentCreatedAfter: p.get('content_created_after') || undefined,
    contentCreatedBefore: p.get('content_created_before') || undefined,
    gradedByUserIds: parseNumberArray(p.get('graded_by_user_ids')),
    gradedAfter: p.get('graded_after') || undefined,
    gradedBefore: p.get('graded_before') || undefined,
  };

  if (anyReviewTypes.length > 0) return applyContentReviewTypesToFilters(base, anyReviewTypes);

  return base;
};

const buildSearchFromFilters = (filters: PeerOptions): string => {
  const p = new URLSearchParams();

  const set = (key: string, value: string | undefined) => {
    if (value) p.set(key, value);
  };

  set('page', filters.page && filters.page > 1 ? String(filters.page) : undefined);
  set('search', filters.search || undefined);

  const userTypeIsAll =
    filters.userType?.length === ALL_USER_TYPES.length && ALL_USER_TYPES.every((v) => filters.userType?.includes(v));
  set('user_type', !userTypeIsAll && filters.userType?.length ? filters.userType.join(',') : undefined);

  // Always write isPeerActive and testUsers when they are set — no "default" suppression
  set('is_peer_active', filters.isPeerActive?.length ? filters.isPeerActive.join(',') : undefined);
  set('test_users', filters.testUsers?.length ? filters.testUsers.join(',') : undefined);

  set(
    'hide_incomplete_peers',
    filters.hideIncompletePeers !== undefined ? String(filters.hideIncompletePeers) : undefined,
  );
  set('has_background_tags', filters.hasBackgroundTags !== undefined ? String(filters.hasBackgroundTags) : undefined);
  set('group_name', filters.groupName || undefined);
  set('tier_ids', filters.tierId?.length ? [...filters.tierId].sort((a, b) => a - b).join(',') : undefined);
  set('age_group', filters.ageGroup?.length ? filters.ageGroup.join(',') : undefined);
  set('gender_tags', filters.genderTags?.length ? [...filters.genderTags].sort((a, b) => a - b).join(',') : undefined);
  set(
    'ethnicity_tags',
    filters.ethnicityTags?.length ? [...filters.ethnicityTags].sort((a, b) => a - b).join(',') : undefined,
  );
  set('family_tags', filters.familyTags?.length ? [...filters.familyTags].sort((a, b) => a - b).join(',') : undefined);
  set(
    'language_tags',
    filters.languageTags?.length ? [...filters.languageTags].sort((a, b) => a - b).join(',') : undefined,
  );
  set(
    'relationship_tags',
    filters.relationshipTags?.length ? [...filters.relationshipTags].sort((a, b) => a - b).join(',') : undefined,
  );
  set(
    'spirituality_tags',
    filters.spiritualityTags?.length ? [...filters.spiritualityTags].sort((a, b) => a - b).join(',') : undefined,
  );
  set(
    'content_tag_ids',
    filters.contentTagIds?.length ? [...filters.contentTagIds].sort((a, b) => a - b).join(',') : undefined,
  );

  // New filters
  set('client_ids', filters.clientIds?.length ? [...filters.clientIds].sort((a, b) => a - b).join(',') : undefined);
  set('domain_id', filters.domainId !== undefined ? String(filters.domainId) : undefined);
  set('tag_ids', filters.tagIds?.length ? [...filters.tagIds].sort((a, b) => a - b).join(',') : undefined);
  set('content_created_after', filters.contentCreatedAfter || undefined);
  set('content_created_before', filters.contentCreatedBefore || undefined);
  set(
    'graded_by_user_ids',
    filters.gradedByUserIds?.length ? [...filters.gradedByUserIds].sort((a, b) => a - b).join(',') : undefined,
  );
  set('graded_after', filters.gradedAfter || undefined);
  set('graded_before', filters.gradedBefore || undefined);

  const anyReviewTypes = deriveContentReviewTypes(filters);
  set('needs_any_content_review_types', anyReviewTypes.length ? anyReviewTypes.join(',') : undefined);

  return p.toString();
};

export const usePeerListingUrlState = () => {
  const location = useLocation();
  const history = useHistory();
  const isUpdatingFromUrl = useRef(false);

  const [filters, setFiltersState] = useState<PeerOptions>(() => {
    if (location.search.length > 1) {
      return parseFiltersFromSearch(location.search);
    }
    return loadFiltersFromStorage() ?? DEFAULT_FILTERS;
  });

  // State → URL + localStorage: whenever filters change, reflect them in the URL and persist
  useEffect(() => {
    const newSearch = buildSearchFromFilters(filters);
    isUpdatingFromUrl.current = true;
    history.replace({ ...location, search: newSearch });
    saveFiltersToStorage(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // URL → State: when the user navigates back/forward or shares a link
  useEffect(() => {
    if (isUpdatingFromUrl.current) {
      isUpdatingFromUrl.current = false;
      return;
    }
    const parsed = parseFiltersFromSearch(location.search);
    if (JSON.stringify(parsed) !== JSON.stringify(filters)) {
      setFiltersState(parsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const setFilters = (updater: React.SetStateAction<PeerOptions>) => {
    setFiltersState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return next;
    });
  };

  return { filters, setFilters };
};
